import { type CSSProperties } from "vue";
import {
    onClickOutside,
  onKeyDown,
  tryOnMounted,
  useElementBounding,
  useElementSize,
  useWindowSize,
} from "@vueuse/core";
import { clamp, lerp, VEC2, type Vector } from "math";
import { useSmoothAnimation } from "../useSmoothAnimation";
import { EventSystem } from "@/utils/event/eventSystem";

export enum EFloaterBehavior {
  DEFAULT = "DEFAULT",
  SNAP = "SNAP",
}

export enum EFloaterAnimationBehavior {
  DEFAULT = "DEFAULT",
  SLIDE_FROM_BASE = "SLIDE_FROM_BASE",
}

export enum EFloaterEvent {
  BEFORE_INACTIVE = 'BEFORE_INACTIVE',
  AFTER_INACTIVE = 'AFTER_INACTIVE'
}

export type FloaterEventMap = {
  [EFloaterEvent.BEFORE_INACTIVE]: { eventType: EFloaterEvent.BEFORE_INACTIVE },
  [EFloaterEvent.AFTER_INACTIVE]: { eventType: EFloaterEvent.AFTER_INACTIVE },
}

export type UseFloaterProps = {
  behavior?: MaybeRef<EFloaterBehavior>;
  animationBehavior?: MaybeRef<EFloaterAnimationBehavior>;
  animationDuration?: number;
  containerRef?: Ref; // The parent of the floating element
  snapRef?: Ref;
  basePosition?: MaybeRef<Vector>; // optional starting position
  targetPosition?: MaybeRef<Vector>; // optional target position
  placementDirection?: MaybeRef<Vector>;
  deactivateOnClickOutside?: boolean;
  ignoreClickOutsideRefs?: Array<Ref>;
  offset?: MaybeRef<Vector>;
  // placement direction:
  //   center: [ 0,  0]
  //   left:   [-1,  0]
  //   right:  [ l,  0]
  //   top:    [ 0, -1]
  //   bottom  [ 0,  1]
};

export type UseFloater = {
  elementProps: ComputedRef<{ style: CSSProperties }>;
  elementRef: Ref;
  setActive: (fn: (isActive: boolean) => boolean) => void;
  isActive: ComputedRef<boolean>;
  addIgnoredClickOutsideRef: (reference: Ref) => void;
  events: EventSystem<FloaterEventMap>;
};

export const useFloater = (props: UseFloaterProps): UseFloater => {
  const events = new EventSystem<FloaterEventMap>();
  const offset = computed(() => unref(props.offset) || VEC2(0, 0));
  const behavior = computed(
    (): EFloaterBehavior => unref(props.behavior) || EFloaterBehavior.DEFAULT,
  );
  const animationDuration = props.animationDuration || 0.333;
  const animationBehavior = computed(
    (): EFloaterAnimationBehavior =>
      unref(props.animationBehavior) || EFloaterAnimationBehavior.DEFAULT,
  );
  const elementRef = ref();
  const elementSizing = useElementSize(elementRef);
  const containerBounds = useElementBounding(props.containerRef);
  const snapBounds = useElementBounding(props.snapRef);
  const winInfo = useWindowSize();
  const activeAnimation = useSmoothAnimation();
  const isActive = ref<boolean>(false);

  const elementSize = computed((): Vector => {
    const w = elementSizing.width.value;
    const h = elementSizing.height.value;
    return VEC2(w, h);
  });

  const containerSize = computed((): Vector => {
    const w = containerBounds.width.value;
    const h = containerBounds.height.value;
    return VEC2(w, h);
  });

  const containerPosition = computed((): Vector => {
    const x = containerBounds.x.value;
    const y = containerBounds.y.value;
    return VEC2(x, y).add(containerSize.value.scale(0.5));
  });

  const snapSize = computed((): Vector => {
    const w = snapBounds.width.value;
    const h = snapBounds.height.value;
    return VEC2(w, h);
  });

  const snapPosition = computed((): Vector => {
    const x = snapBounds.x.value;
    const y = snapBounds.y.value;
    return VEC2(x, y).add(snapSize.value.scale(0.5));
  });

  const placementDirection = computed((): Vector => {
    return unref(props.placementDirection) || VEC2(0, 0);
  });

  const cachedPosition = ref<Vector>(unref(props.basePosition) || VEC2(0, 0));

  // translates the position to the origin of the element
  const fixPositionCenter = (p: Vector) => {
    const elSize = elementSize.value;
    const elCenter = elSize.scale(0.5);
    p = p.sub(elCenter);
    return p;
  };

  // clamps the position of the floating element to never be outside the window
  const clampPosition = (p: Vector) => {
    if (!isActive.value) return p;
    const elSize = elementSize.value;
    p.x = clamp(p.x, 0.0, winInfo.width.value - elSize.x);
    p.y = clamp(p.y, 0.0, winInfo.height.value - elSize.y);
    return p;
  };

  const snapToTarget = (
    targetPos: Vector,
    targetSize: Vector,
    dir: Vector,
  ): Vector => {
    const elSize = elementSize.value;
    const radi = elSize.add(targetSize).mag() * 0.5;
    const p = targetPos.add(dir.scale(radi));
    return clampPosition(
      fixPositionCenter(
        VEC2(
          clamp(
            p.x,
            targetPos.x - 0.5 * targetSize.x - 0.5 * elSize.x,
            targetPos.x + 0.5 * targetSize.x + 0.5 * elSize.x,
          ),
          clamp(
            p.y,
            targetPos.y - 0.5 * targetSize.y - 0.5 * elSize.y,
            targetPos.y + 0.5 * targetSize.y + 0.5 * elSize.y,
          ),
        ),
      ),
    );
  };

  const snappedPosition = computed((): Vector => {
    return snapToTarget(
      snapPosition.value,
      snapSize.value,
      placementDirection.value,
    );
  });

  const basePosition = computed(() => {
    if (
      !isActive.value &&
      animationBehavior.value === EFloaterAnimationBehavior.SLIDE_FROM_BASE
    ) {
      return fixPositionCenter(snapPosition.value);
    }
    if (props.snapRef && behavior.value === EFloaterBehavior.SNAP)
      return snappedPosition.value;

    const userDefined = unref(props.basePosition);
    return clampPosition(
      fixPositionCenter(
        (userDefined ? userDefined : containerPosition.value).clone(),
      ),
    );
  });

  const targetPosition = computed(() => {
    if (props.snapRef && behavior.value === EFloaterBehavior.SNAP) {
      return snappedPosition.value;
    }
    if (!isActive.value) return cachedPosition.value;
    const userDefined = unref(props.targetPosition);
    const p = (
      userDefined ? fixPositionCenter(userDefined) : basePosition.value
    ).clone();
    return clampPosition(p);
  });
  // interpolated from the base position to the target position based on the active on/off animation.
  const currentPosition = computed(() => {
    return basePosition.value.lerp(
      targetPosition.value.add(offset.value),
      activeAnimation.current.value,
    );
  });

  const style = computed((): CSSProperties => {
    const p = currentPosition.value;
    const opacity = lerp(0.0, 100.0, activeAnimation.current.value);
    const scale = lerp(0.0, 100.0, activeAnimation.current.value);

    return {
      position: "fixed",
      zIndex: 504,
      left: p.x + "px",
      top: p.y + "px",
      scale: `${scale}% ${scale}%`,
      opacity: `${opacity}%`,
      ...(isActive.value === false
        ? {
            pointerEvents: "none",
          }
        : {}),
    };
  });

  const elementProps = computed(() => {
    return {
      style: style.value,
    };
  });

  let cleanup: () => void = () => {};

  const setActive = (fn: (isActive: boolean) => boolean) => {
    const nextIsActive = fn(isActive.value);
    if (nextIsActive === isActive.value) return; // Do nothing if the state is the same.

    if (!nextIsActive) {
      events.emit({ eventType: EFloaterEvent.BEFORE_INACTIVE });
    }

    isActive.value = nextIsActive;

    // cleanup old watch() call.
    cleanup();

    if (nextIsActive) {
      Promise.resolve().then(() => {
        cleanup = watch([currentPosition, isActive], ([pos, act]) => {
          if (act) {
            cachedPosition.value = pos;
          }
        });
      });

      activeAnimation.to(1.0, { duration: animationDuration });
    } else {
      activeAnimation.to(0.0, { duration: animationDuration }).then(() => {
        cachedPosition.value = currentPosition.value;
      });
    }

    if (!nextIsActive) {
      events.emit({ eventType: EFloaterEvent.AFTER_INACTIVE });
    }
  };

  onKeyDown("Escape", () => {
    setActive(() => false);
  });

  tryOnMounted(() => {
    setTimeout(() => {
      containerBounds.update();
      snapBounds.update();
    }, 0);
  });


  let cleanupClickOutside: (() => void) = () => {}
  const ignoredRefs = () => [...(props.ignoreClickOutsideRefs || []), ...(props.snapRef ? [props.snapRef] : []), ...(props.containerRef ? [props.containerRef] : [])]

  const createObserver = (ignored: Array<Ref>) => {
    cleanupClickOutside();

    const stop = onClickOutside(elementRef, () => {
      setActive(() => false);
    }, { ignore: ignored });

    cleanupClickOutside = stop;
  }

  if (props.deactivateOnClickOutside) {
    createObserver([...ignoredRefs()]);
  }

  const addIgnoredClickOutsideRef = (reference: Ref) => {
    createObserver([...ignoredRefs(), reference])
  }

  return {
    elementProps,
    elementRef,
    addIgnoredClickOutsideRef,
    setActive,
    isActive: computed((): boolean => isActive.value),
    events
  };
};
